import { useEffect, useState } from 'react';

import {
  ChakraButton,
  ChakraMenu,
  ChakraMenuButton,
  ChakraMenuItem,
  ChakraMenuList,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  FAIconName,
  Icon,
  IconSize,
  Text,
} from '@equip.health/ui';

import { ProfileEventsAdmin } from '~/analytics/profileEvents';
import { TaskEvents } from '~/analytics/taskEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';

import NavTooltip from './navbar/NavToolTip';
import { CREATE_TASK_SOURCES } from '~/lib/constants/task.constants';
const { captionSemibold } = EquipUITheme.typography;

type AddNewCtaProps = {
  isSmall?: boolean;
  setMenuIsOpen?: (open: boolean) => void;
};

const {
  accent: { blue: accentBlue },
  white: whiteColourForIcon,
} = EquipUIFireflyV2Theme.colors;

const CirclePlusIcon = (
  <Icon
    color={whiteColourForIcon}
    name={FAIconName.plusCircleRegular}
    size={IconSize.medium}
  />
);

const ChevronDownIcon = (
  <Icon
    color={whiteColourForIcon}
    name={FAIconName.chevronDownRegular}
    size={IconSize.medium}
  />
);
const AddNewCta = ({ setMenuIsOpen, isSmall = false }: AddNewCtaProps) => {
  const { setComponentVisibility } = useComponentPortalContext();
  const { mode } = useModeContext();
  const { hasManageUserRole } = useGlobalContext();
  const { trackEvent } = useAnalytics();

  const [showAddProfileAccordion, setShowAddProfileAccordion] =
    useState<boolean>(false);

  useEffect(() => {
    if (showAddProfileAccordion) {
      setShowAddProfileAccordion(false);
      setComponentVisibility({
        component: PortalComponent.ADD_EDIT_PROFILE_ACCORDION,
        data: {}, // An empty object is passed to ensure that any existing edit user actions data is cleared.
        isExpanded: true,
        isVisible: true,
        // Without this, certain scenarios may retain stale data, leading to unexpected behavior.
      });
    }
  }, [showAddProfileAccordion, setComponentVisibility]);

  const handleAddNewUser = () => {
    trackEvent(ProfileEventsAdmin.addUserOptionClicked, {
      source: AnalyticsSourceValues.PROFILE_TAB_ADMIN,
    });
    setShowAddProfileAccordion(true);
  };
  const openInquiryDrawer = (): void => {
    setComponentVisibility({
      component: PortalComponent.CREATE_INQUIRY_DRAWER,
      isVisible: true,
    });
  };
  const isAdmin = mode === UserMode.ADMIN;
  const isMaud = mode === UserMode.MAUD;

  return (
    <NavTooltip id="AddNewCta" renderTooltip={isSmall} tooltipText="Create new">
      <ChakraMenu
        onClose={() => setMenuIsOpen?.(false)}
        onOpen={() => setMenuIsOpen?.(true)}
      >
        <ChakraMenuButton
          _focus={isMaud && { backgroundColor: accentBlue }}
          _hover={
            isMaud && {
              backgroundColor: accentBlue,
            }
          }
          as={ChakraButton}
          backgroundColor={isAdmin ? 'primary.100' : accentBlue}
          borderRadius="100px"
          boxShadow="none"
          color="white"
          height="40px"
          leftIcon={CirclePlusIcon}
          paddingRight={isSmall && '10px'}
          rightIcon={!isSmall && ChevronDownIcon}
          width={isSmall ? '0px' : '180px'}
        >
          {!isSmall && (
            <Text color="neutral.background.primary" variant={captionSemibold}>
              Create new
            </Text>
          )}
        </ChakraMenuButton>
        <ChakraMenuList zIndex={101}>
          {mode === UserMode.MAUD && (
            <>
              <ChakraMenuItem
                onClick={() => {
                  trackEvent(
                    TaskEvents.addNewTaskButtonClickedFromCreateNewOptionsMenu,
                    {
                      source: AnalyticsSourceValues.MAUD_LEFT_NAV,
                    },
                  );
                  setComponentVisibility({
                    component: PortalComponent.ADD_TASK_ACCORDION,
                    isExpanded: true,
                    data: {
                      triggerSource: CREATE_TASK_SOURCES.CREATE_NEW_BUTTON,
                    },
                    isVisible: true,
                    showLinkedPatient: false,
                  });
                }}
              >
                Add task
              </ChakraMenuItem>

              <ChakraMenuItem onClick={openInquiryDrawer} style={{ margin: 0 }}>
                Add inquiry
              </ChakraMenuItem>
            </>
          )}
          {mode === UserMode.ADMIN && hasManageUserRole && (
            <ChakraMenuItem onClick={handleAddNewUser} style={{ margin: 0 }}>
              Add user
            </ChakraMenuItem>
          )}
        </ChakraMenuList>
      </ChakraMenu>
    </NavTooltip>
  );
};

export default AddNewCta;
